/**
 * UnitForm — create/edit a CourseUnit.
 * Fields: name (required), description, objectives.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const unitFormSchema = z.object({
  name: z.string().min(1, 'Bắt buộc').max(300),
  description: z.string().optional().or(z.literal('')),
  objectives: z.string().optional().or(z.literal('')),
});

export type UnitFormValues = z.infer<typeof unitFormSchema>;

interface UnitFormProps {
  defaultValues?: Partial<UnitFormValues>;
  isSubmitting?: boolean;
  onSubmit: (values: UnitFormValues) => void;
  onCancel: () => void;
}

export function UnitForm({ defaultValues, isSubmitting, onSubmit, onCancel }: UnitFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UnitFormValues>({
    resolver: zodResolver(unitFormSchema),
    defaultValues: { name: '', description: '', objectives: '', ...defaultValues },
  });

  useEffect(() => {
    reset({ name: '', description: '', objectives: '', ...defaultValues });
  }, [defaultValues, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="unit-name" className="text-sm font-medium">
          Tên chương <span className="text-destructive">*</span>
        </Label>
        <Input id="unit-name" className="h-10" placeholder="Unit 1: Hello!" {...register('name')} />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="unit-description" className="text-sm font-medium">Mô tả</Label>
        <Textarea id="unit-description" rows={2} placeholder="Mô tả ngắn..." {...register('description')} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="unit-objectives" className="text-sm font-medium">Mục tiêu học tập</Label>
        <Textarea id="unit-objectives" rows={3} placeholder="Sau chương này học viên sẽ..." {...register('objectives')} />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lưu'}
        </Button>
      </div>
    </form>
  );
}
